import { useState } from "react";
import { Card, CardContent } from "@/components/ui/library/card";
import { Separator } from "@/components/ui/separator";
import { Accordion } from "@/components/ui/library/accordion";
import TracksHeader from "./components/TracksHeader";
import TrackItem from "./components/TrackItem";
import TrackUserPickerDialog from "./components/TrackUserPickerDialog";
import { useTracksByEntity } from "@/features/stage/api/useTracksPipeline";
import { useEntityTrackMutations } from "@/features/stage/api/useEntityTrackMutations";
import { useLeadTrackFollowUps } from "@/features/stage/api/useLeadTrackFollowUps";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import type { EntityType } from "@/features/stage/types/stageTypes";
import {
  followUpInTrackSlot,
  trackWriteDealId,
} from "@/features/stage/utils/trackGrain";

interface TracksProps {
  entityType?: EntityType;
  entityId?: string | number | null;
}

export default function Tracks({ entityType, entityId }: TracksProps = {}) {
  const leadForm = useLeadForm();
  const childForm = useChildForm();

  const resolvedEntityType: EntityType =
    entityType ?? (childForm?.currentChildId ? "child" : "lead");
  const resolvedEntityId =
    entityId ??
    (resolvedEntityType === "child"
      ? childForm?.currentChildId
      : leadForm?.lead?.id);

  const { tracks, activeTracks, isLoading } = useTracksByEntity(
    resolvedEntityType,
    resolvedEntityId
  );

  const { assignTrack, switchSubTrack, removeTrack, assignTrackUser } =
    useEntityTrackMutations(
    resolvedEntityType,
    resolvedEntityId,
    leadForm?.lead?.id
  );

  const { followUps, reschedule } = useLeadTrackFollowUps(leadForm?.lead?.id);

  const [openValue, setOpenValue] = useState<string | undefined>(undefined);

  // "Who will handle this track?" — only for tracks some role holds at Own,
  // and only for Manage: choosing the member is the head's call.
  // `subTrackId` set = the track is being ADDED; null = hand-over of a track
  // the lead is already on.
  const [picker, setPicker] = useState<{
    trackId: number;
    subTrackId: number | null;
  } | null>(null);
  const pickerTrack = picker
    ? tracks.find((t) => t.id === picker.trackId)
    : undefined;

  // A deal-based track's writes name the deal the tab is open on; a
  // lead-based track's requests carry no deal (exactly as before).
  const dealOf = (trackId: number) => {
    const childId = trackWriteDealId(tracks.find((t) => t.id === trackId));
    return childId != null ? { childId } : {};
  };

  const handleAssignTrack = (trackId: number, subTrackId: number) => {
    const track = tracks.find((t) => t.id === trackId);
    if (track?.hasOwners && (track.accessLevel ?? "edit") === "manage") {
      setPicker({ trackId, subTrackId });
      return;
    }
    // Below Manage nobody is picked by hand: a track with members goes to
    // the next one in turn (as Bulk Update → Tracks does), the server
    // refuses a named pick from anyone but Manage.
    assignTrack.mutate({
      trackId,
      subTrackId,
      ...(track?.hasOwners ? { userId: "auto" as const } : {}),
      ...dealOf(trackId),
    });
  };

  const handlePickerConfirm = (userId: number | "auto" | null) => {
    if (!picker) return;
    const done = { onSettled: () => setPicker(null) };
    if (picker.subTrackId != null) {
      assignTrack.mutate(
        {
          trackId: picker.trackId,
          subTrackId: picker.subTrackId,
          ...(userId != null ? { userId } : {}),
          ...dealOf(picker.trackId),
        },
        done
      );
    } else if (userId != null) {
      assignTrackUser.mutate(
        { trackId: picker.trackId, userId, ...dealOf(picker.trackId) },
        done
      );
    }
  };

  const handleSwitchSubTrack = (trackId: number, subTrackId: number) => {
    switchSubTrack.mutate({ trackId, subTrackId, ...dealOf(trackId) });
  };

  const handleRemoveTrack = (trackId: number) => {
    removeTrack.mutate({ trackId, ...dealOf(trackId) });
  };

  if (isLoading) {
    return (
      <Card className="border-[var(--color-black-1)] rounded-[16px] shadow-none m-4 py-0">
        <CardContent className="py-6 text-center text-sm text-[#505e59]">
          Loading tracks...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[var(--color-black-1)] rounded-[16px] shadow-none m-4 py-0">
      <CardContent className="py-3">
        <TracksHeader
          totalCount={tracks.length}
          assignedCount={activeTracks.length}
        />
        <Separator className="my-4" />

        {tracks.length === 0 ? (
          <p className="text-sm text-[#505e59] text-center py-4">
            No tracks available.
          </p>
        ) : (
          <Accordion
            type="single"
            collapsible
            className="w-full"
            value={openValue}
            onValueChange={setOpenValue}
          >
            {tracks.map((track) => {
              // Role's 4-level track dial for this stage, resolved server-side
              // (denied tracks are filtered out before they reach us). The
              // server enforces the same levels on every mutation — this is
              // the UI honouring them, not the gate.
              // Missing only on a pre-RBAC server; default to the same
              // capped level a new track gets everywhere else.
              const level = track.accessLevel ?? "edit";
              return (
                <TrackItem
                  key={track.id}
                  track={track}
                  // Own = everything Edit allows on the track.
                  canEdit={
                    level === "manage" || level === "own" || level === "edit"
                  }
                  canRemove={level === "manage"}
                  // Choosing who handles the track — an empty seat or a
                  // hand-over — is the head's call (the server enforces it).
                  canReassign={level === "manage"}
                  // Only this row's slot: the lead's rows on a lead-based
                  // track, the open deal's rows on a deal-based one.
                  followUps={followUps.filter((f) => followUpInTrackSlot(track, f))}
                  onRescheduleFollowUp={(id, date, note) =>
                    reschedule.mutate({ id, date, note })
                  }
                  onChangeAssignee={(trackId) =>
                    setPicker({ trackId, subTrackId: null })
                  }
                  onAssignTrack={handleAssignTrack}
                  onSwitchSubTrack={handleSwitchSubTrack}
                  onRemoveTrack={handleRemoveTrack}
                />
              );
            })}
          </Accordion>
        )}

        <TrackUserPickerDialog
          open={picker !== null}
          onOpenChange={(open) => !open && setPicker(null)}
          leadId={leadForm?.lead?.id}
          trackId={picker?.trackId ?? null}
          trackName={
            pickerTrack?.grain === "deal" && pickerTrack.scope?.childName
              ? `${pickerTrack.name} on ${pickerTrack.scope.childName}`
              : pickerTrack?.name
          }
          current={pickerTrack?.assignees}
          mode={picker?.subTrackId != null ? "add" : "change"}
          isSaving={assignTrack.isPending || assignTrackUser.isPending}
          onConfirm={handlePickerConfirm}
        />
      </CardContent>
    </Card>
  );
}
